import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Tag, RefreshCw } from "lucide-react";
import {
  AssetCategoryView,
  AssetLocationView,
  CreateAssetCategoryInput,
  UpdateAssetCategoryInput,
  getCategories,
  createCategory,
  updateCategory,
  archiveCategory,
  getAssetLocations,
  getCategoryIdsByLocation,
} from "../../../../api/General/assets";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/Dialog";
import Button from "@/components/ui/Button";
import AlertDialog from "@/components/ui/AlertDialog";

interface AssetCategoriesTabProps {
  onCategoriesChanged?: () => void;
}

interface CategoryNode extends AssetCategoryView {
  children: CategoryNode[];
}

const MAX_LEVEL = 3;

function buildTree(categories: AssetCategoryView[]): CategoryNode[] {
  const nodes = new Map<number, CategoryNode>();
  categories.forEach((c) => nodes.set(c.id, { ...c, children: [] }));

  const roots: CategoryNode[] = [];
  categories.forEach((c) => {
    const node = nodes.get(c.id)!;
    const parent = c.parentId != null ? nodes.get(c.parentId) : undefined;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortRec = (list: CategoryNode[]) => {
    list.sort((a, b) => a.name.localeCompare(b.name));
    list.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

function flattenVisible(
  nodes: CategoryNode[],
  expanded: Record<number, boolean>,
  depth = 0
): Array<{ node: CategoryNode; depth: number }> {
  const out: Array<{ node: CategoryNode; depth: number }> = [];
  for (const n of nodes) {
    out.push({ node: n, depth });
    if (n.children.length > 0 && expanded[n.id]) {
      out.push(...flattenVisible(n.children, expanded, depth + 1));
    }
  }
  return out;
}

export default function AssetCategoriesTab({ onCategoriesChanged }: AssetCategoriesTabProps) {
  const { t } = useTranslation();

  const [categories, setCategories] = useState<AssetCategoryView[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  // Filters
  const [locations, setLocations] = useState<AssetLocationView[]>([]);
  const [filterL1Id, setFilterL1Id] = useState<number | undefined>(undefined);
  const [filterL2Id, setFilterL2Id] = useState<number | undefined>(undefined);
  const [filterL3Id, setFilterL3Id] = useState<number | undefined>(undefined);
  const [filterLocationId, setFilterLocationId] = useState<number | undefined>(undefined);
  const [locationCategoryIds, setLocationCategoryIds] = useState<Set<number> | null>(null);
  const [locationFilterLoading, setLocationFilterLoading] = useState(false);

  // Modal state
  const [formOpen, setFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<AssetCategoryView | null>(null);
  const [saving, setSaving] = useState(false);

  // Form fields
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [level, setLevel] = useState(1);
  const [parentId, setParentId] = useState<number | undefined>(undefined);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Toast
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);

  // Archive confirmation
  const [archiveTarget, setArchiveTarget] = useState<AssetCategoryView | null>(null);
  const [archiving, setArchiving] = useState(false);

  const showToast = useCallback((message: string, type: "success" | "error") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const loadCategories = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getCategories();
      if (result.errors.length === 0) {
        setCategories(result.payload);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    (async () => {
      const result = await getAssetLocations();
      if (result.errors.length === 0) setLocations(result.payload);
    })();
  }, []);

  useEffect(() => {
    if (!filterLocationId) {
      setLocationCategoryIds(null);
      setLocationFilterLoading(false);
      return;
    }
    let cancelled = false;
    setLocationFilterLoading(true);
    (async () => {
      const result = await getCategoryIdsByLocation(filterLocationId);
      if (!cancelled) {
        if (result.errors.length === 0) setLocationCategoryIds(new Set(result.payload));
        setLocationFilterLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filterLocationId]);

  const filterL1Options = useMemo(() => categories.filter((c) => c.level === 1), [categories]);
  const filterL2Options = useMemo(
    () => categories.filter((c) => c.level === 2 && c.parentId === filterL1Id),
    [categories, filterL1Id]
  );
  const filterL3Options = useMemo(
    () => categories.filter((c) => c.level === 3 && c.parentId === filterL2Id),
    [categories, filterL2Id]
  );

  // Level filters narrow to the selected node plus its descendants; picking a broader level clears the narrower ones.
  const levelFilteredCategories = useMemo(() => {
    if (filterL3Id) return categories.filter((c) => c.id === filterL3Id);
    if (filterL2Id) return categories.filter((c) => c.id === filterL2Id || c.parentId === filterL2Id);
    if (filterL1Id) {
      return categories.filter((c) => {
        if (c.id === filterL1Id || c.parentId === filterL1Id) return true;
        const parent = categories.find((p) => p.id === c.parentId);
        return parent?.parentId === filterL1Id;
      });
    }
    return categories;
  }, [categories, filterL1Id, filterL2Id, filterL3Id]);

  // Location filter narrows further to Level-3 items that actually have stock there.
  const filteredCategories = useMemo(() => {
    if (!filterLocationId || !locationCategoryIds) return levelFilteredCategories;
    return levelFilteredCategories.filter((c) => c.level === 3 && locationCategoryIds.has(c.id));
  }, [levelFilteredCategories, filterLocationId, locationCategoryIds]);

  const isFiltering = !!(filterL1Id || filterL2Id || filterL3Id || filterLocationId);

  // With a location filter active, filteredCategories is L3-only, so buildTree renders those as flat roots without their L1/L2 ancestors — intentional.
  const tree = useMemo(() => buildTree(filteredCategories), [filteredCategories]);
  const visibleRows = useMemo(() => flattenVisible(tree, expanded), [tree, expanded]);

  // Auto-expand matched nodes so filter results are visible without manual clicking.
  useEffect(() => {
    if (!isFiltering) return;
    setExpanded((prev) => {
      const next = { ...prev };
      filteredCategories.forEach((c) => { next[c.id] = true; });
      return next;
    });
  }, [isFiltering, filteredCategories]);

  const handleFilterL1Change = (val: number | undefined) => {
    setFilterL1Id(val);
    setFilterL2Id(undefined);
    setFilterL3Id(undefined);
  };

  const handleFilterL2Change = (val: number | undefined) => {
    setFilterL2Id(val);
    setFilterL3Id(undefined);
  };

  const resetFilters = () => {
    setFilterL1Id(undefined);
    setFilterL2Id(undefined);
    setFilterL3Id(undefined);
    setFilterLocationId(undefined);
  };

  const parentOptions = useMemo(
    () => categories.filter((c) => c.level === level - 1),
    [categories, level]
  );

  // A level is only offerable once something exists to hang it from. Without this the form let a
  // user pick Level 2 with no families defined, then refused to submit against an empty parent
  // dropdown with no way forward — the level itself is the thing that has to be blocked, not the
  // save. Level 1 is always available because it is the root.
  const levelHasParents = useCallback(
    (lvl: number) => lvl === 1 || categories.some((c) => c.level === lvl - 1),
    [categories]
  );

  const toggleExpand = (id: number) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const openAdd = (defaultLevel = 1, defaultParentId?: number) => {
    setEditingCategory(null);
    setName("");
    setDescription("");
    setLevel(defaultLevel);
    setParentId(defaultParentId);
    setErrors({});
    setFormOpen(true);
  };

  const openAddChild = (parent: CategoryNode) => {
    openAdd((parent.level ?? 1) + 1, parent.id);
  };

  const openEdit = (cat: AssetCategoryView) => {
    setEditingCategory(cat);
    setName(cat.name);
    setDescription(cat.description ?? "");
    setLevel(cat.level ?? 1);
    setParentId(cat.parentId);
    setErrors({});
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingCategory(null);
  };

  const handleLevelChange = (nextLevel: number) => {
    setLevel(nextLevel);
    // Reset the parent selection if it's no longer valid for the new level
    const stillValid = categories.some((c) => c.id === parentId && c.level === nextLevel - 1);
    if (!stillValid) setParentId(undefined);
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = t("assetCategories.nameRequired");
    if (level > 1 && !parentId) errs.parentId = t("assetCategories.parentRequired");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      if (editingCategory) {
        const input: UpdateAssetCategoryInput = {
          id: editingCategory.id,
          name: name.trim(),
          description: description.trim() || undefined,
          level,
          parentId: level > 1 ? parentId : undefined,
        };
        const result = await updateCategory(input);
        if (result.errors.length === 0) {
          showToast(t("assetCategories.categoryUpdated"), "success");
          closeForm();
          loadCategories();
          onCategoriesChanged?.();
        } else {
          showToast(result.errors[0]?.description || t("Something went wrong"), "error");
        }
      } else {
        const input: CreateAssetCategoryInput = {
          name: name.trim(),
          description: description.trim() || undefined,
          level,
          parentId: level > 1 ? parentId : undefined,
        };
        const result = await createCategory(input);
        if (result.errors.length === 0) {
          showToast(t("assetCategories.categoryCreated"), "success");
          closeForm();
          if (parentId != null) setExpanded((p) => ({ ...p, [parentId]: true }));
          loadCategories();
          onCategoriesChanged?.();
        } else {
          showToast(result.errors[0]?.description || t("Something went wrong"), "error");
        }
      }
    } catch {
      showToast(t("Something went wrong"), "error");
    } finally {
      setSaving(false);
    }
  };

  // Open the confirmation dialog; the actual call happens in confirmArchive
  const handleArchive = (cat: AssetCategoryView) => {
    setArchiveTarget(cat);
  };

  const confirmArchive = async () => {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      const result = await archiveCategory(archiveTarget.id);
      if (result.errors.length === 0) {
        showToast(t("assetCategories.categoryArchived"), "success");
        loadCategories();
        onCategoriesChanged?.();
      } else {
        showToast(result.errors[0]?.description || t("Something went wrong"), "error");
      }
    } catch {
      showToast(t("Something went wrong"), "error");
    } finally {
      setArchiving(false);
      setArchiveTarget(null);
    }
  };

  const levelLabel = (lvl?: number) => {
    switch (lvl) {
      case 1: return t("assetCategories.level1");
      case 2: return t("assetCategories.level2");
      case 3: return t("assetCategories.level3");
      default: return "";
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-foreground">{t("assetCategories.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("assetCategories.subtitle")}</p>
        </div>
        <Button
          variant="outline"
          onClick={() => openAdd(1)}
          data-testid="add-category-btn"
        >
          <svg className="w-4 h-4 me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t("assetCategories.addCategory")}
        </Button>
      </div>

      {/* Filters */}
      {!loading && categories.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={filterL1Id ?? ""}
            onChange={(e) => handleFilterL1Change(e.target.value ? Number(e.target.value) : undefined)}
            className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            data-testid="category-filter-l1"
          >
            <option value="">{t("assets.allCategoryGroups")}</option>
            {filterL1Options.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>

          <select
            value={filterL2Id ?? ""}
            onChange={(e) => handleFilterL2Change(e.target.value ? Number(e.target.value) : undefined)}
            disabled={!filterL1Id}
            className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed"
            data-testid="category-filter-l2"
          >
            <option value="">{t("assets.allCategoryTypes")}</option>
            {filterL2Options.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>

          <select
            value={filterL3Id ?? ""}
            onChange={(e) => setFilterL3Id(e.target.value ? Number(e.target.value) : undefined)}
            disabled={!filterL2Id}
            className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed"
            data-testid="category-filter-l3"
          >
            <option value="">{t("assets.allCategoryModels")}</option>
            {filterL3Options.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>

          <select
            value={filterLocationId ?? ""}
            onChange={(e) => setFilterLocationId(e.target.value ? Number(e.target.value) : undefined)}
            className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
            data-testid="category-filter-location"
          >
            <option value="">{t("assets.allLocations")}</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>{loc.name}</option>
            ))}
          </select>

          {isFiltering && (
            <button
              type="button"
              onClick={resetFilters}
              title={t("assets.resetFilters")}
              aria-label={t("assets.resetFilters")}
              className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
              data-testid="category-filters-reset"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
        </div>
      )}

      {/* Tree */}
      {loading || locationFilterLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : categories.length === 0 ? (
        <div className="text-center py-12">
          <Tag className="mx-auto h-12 w-12 text-muted-foreground" />
          <h3 className="mt-2 text-sm font-medium text-foreground">{t("assetCategories.noCategories")}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t("assetCategories.noCategoriesDescription")}</p>
        </div>
      ) : filteredCategories.length === 0 ? (
        <div className="text-center py-12" data-testid="category-filter-empty">
          <Tag className="mx-auto h-12 w-12 text-muted-foreground" />
          <p className="mt-2 text-sm text-muted-foreground">{t("assetCategories.noneAtLocation")}</p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("assetCategories.name")}
                </th>
                <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("assetCategories.description")}
                </th>
                <th className="px-4 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("assetCategories.assetCount")}
                </th>
                <th className="px-4 py-3 text-end text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t("assetCategories.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="bg-card divide-y divide-border">
              {visibleRows.map(({ node, depth }) => {
                const hasChildren = node.children.length > 0;
                const isOpen = !!expanded[node.id];
                const isLeaf = (node.level ?? 1) >= MAX_LEVEL;
                return (
                  <tr key={node.id} className="hover:bg-muted/30 transition-colors" data-testid={`category-row-${node.id}`}>
                    <td className="px-4 py-3 text-sm font-medium text-foreground">
                      <div className="flex items-center" style={{ paddingInlineStart: depth * 20 }}>
                        {hasChildren ? (
                          <button
                            type="button"
                            onClick={() => toggleExpand(node.id)}
                            className="me-1.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                            aria-expanded={isOpen}
                            data-testid={`expand-category-${node.id}`}
                          >
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        ) : (
                          <span className="w-4 h-4 me-1.5 inline-block" />
                        )}
                        <span>{node.name}</span>
                        <span className="ms-2 inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                          {levelLabel(node.level)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {node.description || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">
                      {isLeaf ? (
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
                          data-testid={`category-available-${node.id}`}
                          title={t("assetCategories.availableOfTotal", { available: node.availableCount, total: node.totalOwnedCount })}
                        >
                          {node.availableCount}/{node.totalOwnedCount}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          {node.assetCount}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-end">
                      <div className="flex items-center justify-end gap-2">
                        {(node.level ?? 1) < MAX_LEVEL && (
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={() => openAddChild(node)}
                            data-testid={`add-subcategory-${node.id}`}
                          >
                            <svg className="w-3.5 h-3.5 me-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            {t("assetCategories.addSubcategory")}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => openEdit(node)}
                          data-testid={`edit-category-${node.id}`}
                        >
                          <svg className="w-3.5 h-3.5 me-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          {t("common.edit")}
                        </Button>
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => handleArchive(node)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`archive-category-${node.id}`}
                        >
                          <svg className="w-3.5 h-3.5 me-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                          {t("assets.archive")}
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit Modal */}
      <Dialog open={formOpen} onOpenChange={(o) => !o && closeForm()}>
        <DialogContent size="default">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? t("assetCategories.editCategory") : t("assetCategories.addCategory")}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <DialogBody className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t("assetCategories.name")} *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary ${errors.name ? "border-destructive" : "border-border"}`}
                  data-testid="category-name-input"
                  autoFocus
                />
                {errors.name && <p className="text-destructive text-xs mt-1">{errors.name}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {t("assetCategories.level")} *
                  </label>
                  <select
                    value={level}
                    onChange={(e) => handleLevelChange(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary bg-card"
                    data-testid="category-level-select"
                  >
                    <option value={1}>{t("assetCategories.level1Option")}</option>
                    <option value={2} disabled={!levelHasParents(2)}>{t("assetCategories.level2Option")}</option>
                    <option value={3} disabled={!levelHasParents(3)}>{t("assetCategories.level3Option")}</option>
                  </select>
                </div>

                {level > 1 && (
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      {t("assetCategories.parent")} *
                    </label>
                    <select
                      value={parentId ?? ""}
                      onChange={(e) => setParentId(e.target.value ? Number(e.target.value) : undefined)}
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary bg-card ${errors.parentId ? "border-destructive" : "border-border"}`}
                      data-testid="category-parent-select"
                    >
                      <option value="">{t("assetCategories.selectParent")}</option>
                      {parentOptions.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    {errors.parentId && <p className="text-destructive text-xs mt-1">{errors.parentId}</p>}
                    {parentOptions.length === 0 && (
                      <p className="text-muted-foreground text-xs mt-1">{t("assetCategories.noParentOptions")}</p>
                    )}
                  </div>
                )}
              </div>

              <p className="text-xs text-muted-foreground" data-testid="category-level-help">
                {t("assetCategories.levelHelp")}
              </p>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t("assetCategories.description")}
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
                  data-testid="category-description-input"
                />
              </div>
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={closeForm}
                data-testid="category-form-cancel"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                loading={saving}
                data-testid="category-form-save"
              >
                {saving ? t("common.saving") : editingCategory ? t("common.save") : t("assetCategories.addCategory")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation */}
      <AlertDialog
        open={!!archiveTarget}
        onOpenChange={(open) => { if (!open) setArchiveTarget(null); }}
        title={t("assets.archive")}
        description={t("assetCategories.confirmArchive")}
        variant="destructive"
        confirmLabel={t("assets.archive")}
        cancelLabel={t("common.cancel")}
        isLoading={archiving}
        onConfirm={confirmArchive}
      />

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 end-6 z-[60] px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
            toast.type === "success" ? "bg-success/10 text-success border border-success/20" : "bg-destructive/10 text-destructive border border-destructive/20"
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
